import { motion } from 'framer-motion';
import { ArrowRight, Clock, MapPin, TrendingUp, Shield } from 'lucide-react';
import monacoImage from '@/assets/monaco-penthouse.jpg';
import dubaiImage from '@/assets/palm-jumeirah-villa.jpg';
import londonImage from '@/assets/london-belgravia-mansion.jpg';

const caseStudies = [
  {
    image: monacoImage,
    location: 'Monaco, Carré d\'Or',
    timeline: '87 days',
    title: 'Penthouse acquisition in Monaco\'s most exclusive district',
    brief: 'A European industrialist sought a principal residence in Monaco with port views and proximity to the Casino. Our advisory team identified an off-market opportunity through our Monaco partner network, negotiated a €2.1M reduction from asking price, and coordinated the full notarial process — from initial viewing to key handover in under 90 days.',
    stats: [
      { label: 'Acquisition Value', value: '€18.5M' },
      { label: 'Below Asking', value: '€2.1M' },
      { label: 'Time to Close', value: '87 days' },
    ],
    tag: 'Principal Residence',
  },
  {
    image: dubaiImage,
    location: 'Dubai, Palm Jumeirah',
    timeline: '62 days',
    title: 'Portfolio expansion into Dubai\'s Golden Visa market',
    brief: 'A family office managing multi-generational wealth identified Dubai as a tax-efficient diversification opportunity. We matched them with our local partner who sourced a signature villa on the Palm — securing Golden Visa eligibility, arranging property management, and structuring the acquisition through a UAE-domiciled entity for optimal tax positioning.',
    stats: [
      { label: 'Acquisition Value', value: '€12.8M' },
      { label: 'Projected Yield', value: '6.2%' },
      { label: 'Time to Close', value: '62 days' },
    ],
    tag: 'Investment & Visa',
  },
  {
    image: londonImage,
    location: 'London, Belgravia',
    timeline: '114 days',
    title: 'Historic mansion acquisition with full renovation coordination',
    brief: 'A Middle Eastern client sought a legacy property in London\'s most prestigious garden square. Our UK partner identified a Grade II-listed mansion before it reached the open market. We coordinated heritage planning consent, introduced specialist renovation architects, and managed the conveyancing process — delivering a turnkey residence within four months.',
    stats: [
      { label: 'Acquisition Value', value: '£24.5M' },
      { label: 'Renovation Budget', value: '£3.2M' },
      { label: 'Time to Close', value: '114 days' },
    ],
    tag: 'Legacy Property',
  },
];

const CaseStudiesSection = () => {
  return (
    <section className="py-24 md:py-32 bg-charcoal text-cream">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-16 md:mb-20"
        >
          <span className="font-body text-gold text-sm uppercase tracking-[0.2em] mb-4 block">
            Client Journeys
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light mb-6">
            From first inquiry to{' '}
            <span className="text-gold">key handover</span>
          </h2>
          <p className="text-cream/60 text-base md:text-lg leading-relaxed font-body">
            Each acquisition is unique. These anonymised case studies illustrate how our advisory framework 
            and local partner network deliver precision, discretion, and measurable advantage at every stage.
          </p>
        </motion.div>

        {/* Case Studies */}
        <div className="space-y-16 md:space-y-24">
          {caseStudies.map((study, index) => (
            <motion.article
              key={study.location}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center ${
                index % 2 === 1 ? 'lg:direction-rtl' : ''
              }`}
            >
              {/* Image */}
              <div className={`relative overflow-hidden rounded-xl ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="aspect-[4/3] relative">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-transparent" />
                  
                  {/* Tag overlay */}
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="px-3 py-1.5 bg-gold/90 text-charcoal text-xs font-body font-medium uppercase tracking-wider rounded-full">
                      {study.tag}
                    </span>
                  </div>

                  {/* Location & timeline overlay */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-cream/90">
                      <MapPin className="w-3.5 h-3.5" />
                      <span className="text-sm font-body">{study.location}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-cream/90">
                      <Clock className="w-3.5 h-3.5" />
                      <span className="text-sm font-body">{study.timeline}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <h3 className="font-display text-xl md:text-2xl lg:text-3xl font-light mb-5 leading-snug">
                  {study.title}
                </h3>
                <p className="text-cream/60 font-body text-sm md:text-base leading-relaxed mb-8">
                  {study.brief}
                </p>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {study.stats.map((stat) => (
                    <div key={stat.label} className="border-l border-gold/30 pl-4">
                      <div className="font-display text-lg md:text-xl text-gold font-light">
                        {stat.value}
                      </div>
                      <div className="text-cream/40 text-xs font-body uppercase tracking-wider mt-1">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Trust indicators */}
                <div className="flex flex-wrap gap-4 text-cream/40 text-xs font-body">
                  <div className="flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5 text-gold/60" />
                    <span>Client identity protected</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5 text-gold/60" />
                    <span>Verified transaction</span>
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-16 md:mt-24 text-center"
        >
          <p className="text-cream/50 font-body text-sm mb-6">
            Your acquisition story begins with a single conversation.
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 font-body text-gold text-sm uppercase tracking-[0.15em] hover:text-gold-light transition-colors group"
          >
            Begin your journey
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default CaseStudiesSection;
